/**
 * Align a DSM roof mesh the same way the 3D viewer does, then place panels.
 * The satellite tab uses this without opening the 3D view.
 */

import * as THREE from "three";
import { DEFAULT_LAYOUT_CONFIG } from "@/utils/panelLayoutCalculator";
import {
  boundaryWorldRings,
  staticMapOverlayShiftX,
  type MeshGroundFrame,
} from "@/utils/satelliteGroundPlane";
import {
  placePanelsOnRoofTriangles,
  type MeshTriangle,
  type PanelMount,
  type RoofMeshLayout,
} from "@/utils/roofMeshPanelPlacement";

export type RoofMeshAlign = {
  x: number;
  z: number;
  baseY: number;
  shiftX: number;
};

export type RoofBoundary = Parameters<typeof boundaryWorldRings>[0];

const panelLayoutCache = new Map<string, Record<PanelMount, RoofMeshLayout>>();

export function clearRoofMeshLayoutCache() {
  panelLayoutCache.clear();
}

/**
 * Rotate elevation up, mirror east-west, centre the building, and clip to the title.
 * Mutates `mesh`. Panel positions are in this centred frame.
 */
export function prepareRoofMesh(
  mesh: THREE.Object3D,
  options: {
    groundFrame: MeshGroundFrame | null;
    propertyBoundary: RoofBoundary;
    satelliteImageUrl: string | null;
    cacheKey: string;
  },
): { align: RoofMeshAlign; cacheKey: string } {
  mesh.rotation.x = -Math.PI / 2;
  mesh.scale.z = 1;
  mesh.scale.x = -1;
  mesh.updateMatrixWorld(true);

  const box = new THREE.Box3().setFromObject(mesh);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  mesh.position.sub(center);
  mesh.updateMatrixWorld(true);

  let clipped = false;
  const alignCenter = { x: center.x, z: center.z };
  const shiftX = options.groundFrame
    ? staticMapOverlayShiftX(options.satelliteImageUrl, options.groundFrame, alignCenter)
    : 0;
  if (options.groundFrame && options.propertyBoundary) {
    const rings = boundaryWorldRings(options.propertyBoundary, options.groundFrame, alignCenter);
    const ring = largestRing(rings);
    if (ring) {
      clipMeshToRing(mesh, ring.map((point) => ({ x: point.x + shiftX, z: point.z })));
      clipped = true;
    }
  }

  return {
    align: { x: center.x, z: center.z, baseY: -size.y / 2, shiftX },
    cacheKey: clipped ? `${options.cacheKey}#plot-boundary` : options.cacheKey,
  };
}

export function layoutsForMesh(cacheKey: string, mesh: THREE.Object3D): Record<PanelMount, RoofMeshLayout> {
  const cached = panelLayoutCache.get(cacheKey);
  if (cached) return cached;

  const triangles = collectMeshTriangles(mesh);
  const layouts: Record<PanelMount, RoofMeshLayout> = {
    portrait: placePanelsOnRoofTriangles(triangles, DEFAULT_LAYOUT_CONFIG, "portrait"),
    landscape: placePanelsOnRoofTriangles(triangles, DEFAULT_LAYOUT_CONFIG, "landscape"),
  };
  panelLayoutCache.set(cacheKey, layouts);
  if (panelLayoutCache.size > 8) {
    const oldest = panelLayoutCache.keys().next().value;
    if (oldest) panelLayoutCache.delete(oldest);
  }
  return layouts;
}

function largestRing(rings: Array<Array<{ x: number; z: number }>>): Array<{ x: number; z: number }> | null {
  let best: Array<{ x: number; z: number }> | null = null;
  let bestArea = 0;
  for (const ring of rings) {
    let area = 0;
    for (let index = 0; index < ring.length; index++) {
      const next = ring[(index + 1) % ring.length];
      area += ring[index].x * next.z - next.x * ring[index].z;
    }
    const size = Math.abs(area);
    if (size > bestArea) {
      best = ring;
      bestArea = size;
    }
  }
  return best;
}

function pointInRing(x: number, z: number, ring: Array<{ x: number; z: number }>): boolean {
  let inside = false;
  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
    const current = ring[index];
    const prior = ring[previous];
    const crosses = (current.z > z) !== (prior.z > z)
      && x < ((prior.x - current.x) * (z - current.z)) / ((prior.z - current.z) || 1e-12) + current.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

function clipMeshToRing(root: THREE.Object3D, ring: Array<{ x: number; z: number }>) {
  const inverse = new THREE.Matrix4();
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  root.updateMatrixWorld(true);
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const position = mesh.geometry.getAttribute("position");
    if (!position) return;
    const color = mesh.geometry.getAttribute("color");
    const index = mesh.geometry.getIndex();
    const triangleCount = index ? index.count / 3 : position.count / 3;
    inverse.copy(mesh.matrixWorld).invert();
    const nextPosition: number[] = [];
    const nextColor: number[] = [];
    let kept = 0;
    const worldOf = (vertex: number, target: THREE.Vector3) => {
      target.fromBufferAttribute(position, vertex).applyMatrix4(mesh.matrixWorld);
      return target;
    };
    for (let triangle = 0; triangle < triangleCount; triangle++) {
      const ia = index ? index.getX(triangle * 3) : triangle * 3;
      const ib = index ? index.getX(triangle * 3 + 1) : triangle * 3 + 1;
      const ic = index ? index.getX(triangle * 3 + 2) : triangle * 3 + 2;
      worldOf(ia, a);
      worldOf(ib, b);
      worldOf(ic, c);
      const centroidX = (a.x + b.x + c.x) / 3;
      const centroidZ = (a.z + b.z + c.z) / 3;
      if (!pointInRing(centroidX, centroidZ, ring)) continue;
      kept += 1;
      for (const world of [a, b, c]) {
        const local = world.clone().applyMatrix4(inverse);
        nextPosition.push(local.x, local.y, local.z);
      }
      if (color) {
        for (const vertex of [ia, ib, ic]) {
          nextColor.push(color.getX(vertex), color.getY(vertex), color.getZ(vertex));
        }
      }
    }
    if (kept === 0 || kept === triangleCount) return;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(nextPosition, 3));
    if (nextColor.length > 0) {
      geometry.setAttribute("color", new THREE.Float32BufferAttribute(nextColor, 3));
    }
    mesh.geometry.dispose();
    mesh.geometry = geometry;
  });
}

function collectMeshTriangles(root: THREE.Object3D): MeshTriangle[] {
  const triangles: MeshTriangle[] = [];
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  root.updateMatrixWorld(true);
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const position = mesh.geometry.getAttribute("position");
    if (!position) return;
    const index = mesh.geometry.getIndex();
    const read = (vertex: number, target: THREE.Vector3) => {
      target.fromBufferAttribute(position, vertex).applyMatrix4(mesh.matrixWorld);
      return { x: target.x, y: target.y, z: target.z };
    };
    if (index) {
      for (let i = 0; i < index.count; i += 3) {
        triangles.push([
          read(index.getX(i), a),
          read(index.getX(i + 1), b),
          read(index.getX(i + 2), c),
        ]);
      }
    } else {
      for (let i = 0; i < position.count; i += 3) {
        triangles.push([read(i, a), read(i + 1, b), read(i + 2, c)]);
      }
    }
  });
  return triangles;
}
